/**
 * Full & Final settlement, as data and pure functions (ADR 0015).
 *
 *     initiated ─▶ clearance_review ─▶ calculated ─▶ submitted ─▶ approved ─▶ payment_processing ─▶ paid
 *          │               │               ▲ │            │  │          │
 *          └───────────────┴──▶ cancelled  │ └─(recalc)   │  └▶ rejected ─▶ calculated | cancelled
 *                                          └──────────────┴── a financial input changed: approval invalidated
 *
 * A SETTLEMENT is one record per exit; its money lives in immutable CALCULATION VERSIONS. Every
 * recalculation is a new version, and an approval binds to one version and its digest: when a financial
 * input changes, the settlement goes back to `calculated` and whatever was approved is no longer valid.
 * Once payment has started (`payment_processing`) the approved version is final — the payment service
 * owns it from there, and a correction is a new, separate settlement action, not an edit.
 *
 * WHAT THIS MODULE DECIDES and every implementation must agree with: the stages and their edges, how the
 * legacy four-value `status` column is kept truthful, the calculation item shape, exact totals, what a
 * clearance gate blocks, whether an approval still stands, and what counts as a duplicate of regular
 * payroll.
 *
 * WHAT IT DOES NOT DO: it computes no gratuity, no encashment and no tax — those are the engine's, and a
 * missing rule is a PROBLEM on the version (`incomplete`), never a guessed number. It hashes nothing (the
 * digest is server-side) and reads no clock: dates are `YYYY-MM-DD` strings compared as strings.
 *
 * RN-SAFE: no Node built-ins, no I/O, and money only as decimal STRINGS, added exactly with BigInt.
 */

import type { PaymentObligationState } from './payroll-payments.js';

// ---------------------------------------------------------------------------
// Stages
// ---------------------------------------------------------------------------

export const FNF_STAGES = [
  'initiated',
  'clearance_review',
  'calculated',
  'submitted',
  'approved',
  'payment_processing',
  'paid',
  'rejected',
  'cancelled',
] as const;
export type FnfStage = (typeof FNF_STAGES)[number];

export interface FnfTransition {
  readonly from: FnfStage;
  readonly to: FnfStage;
  /** Who or what makes the step, for the reader; the routes enforce it. */
  readonly by: string;
}

export const FNF_STAGE_TRANSITIONS: readonly FnfTransition[] = [
  { from: 'initiated', to: 'clearance_review', by: 'HR opens the departmental clearances' },
  { from: 'initiated', to: 'calculated', by: 'HR/payroll calculates (no clearances configured)' },
  { from: 'initiated', to: 'cancelled', by: 'HR/payroll, with a reason' },
  { from: 'clearance_review', to: 'calculated', by: 'HR/payroll calculates' },
  { from: 'clearance_review', to: 'cancelled', by: 'HR/payroll, with a reason' },
  { from: 'calculated', to: 'calculated', by: 'recalculation: a new version supersedes the last' },
  { from: 'calculated', to: 'submitted', by: 'HR/payroll submits a ready version for approval' },
  { from: 'calculated', to: 'cancelled', by: 'HR/payroll, with a reason' },
  { from: 'submitted', to: 'approved', by: 'owner approves the exact version and digest' },
  { from: 'submitted', to: 'rejected', by: 'owner rejects, with a comment' },
  {
    from: 'submitted',
    to: 'calculated',
    by: 'a financial input changed: the submission is invalidated',
  },
  { from: 'approved', to: 'payment_processing', by: 'the payment obligation entered a batch' },
  { from: 'approved', to: 'calculated', by: 'a financial input changed before payment started' },
  {
    from: 'payment_processing',
    to: 'paid',
    by: 'the obligation was paid (confirmed reconciliation)',
  },
  { from: 'rejected', to: 'calculated', by: 'HR/payroll recalculates' },
  { from: 'rejected', to: 'cancelled', by: 'HR/payroll, with a reason' },
];

export function isFnfTransition(from: FnfStage, to: FnfStage): boolean {
  return FNF_STAGE_TRANSITIONS.some((e) => e.from === from && e.to === to);
}

export function nextFnfStages(from: FnfStage): FnfStage[] {
  return FNF_STAGE_TRANSITIONS.filter((e) => e.from === from).map((e) => e.to);
}

/** Stages with no way out. Everything else is "active" and blocks a second settlement for the exit. */
export const TERMINAL_FNF_STAGES: ReadonlySet<FnfStage> = new Set(['paid', 'cancelled']);

export function isActiveFnfStage(stage: FnfStage): boolean {
  return !TERMINAL_FNF_STAGES.has(stage);
}

/** Before submission: the stages in which financial inputs are still being assembled. */
export const PRE_SUBMISSION_FNF_STAGES: ReadonlySet<FnfStage> = new Set([
  'initiated',
  'clearance_review',
  'calculated',
  'rejected',
]);

// ---------------------------------------------------------------------------
// The legacy `status` column
// ---------------------------------------------------------------------------

/**
 * The four values the legacy `fnf_settlements.status` column and the legacy
 * `/employees/fnf-settlements` DTO know. They stay correct for every settlement, old or new.
 */
export const FNF_LEGACY_STATUSES = ['pending_approval', 'approved', 'rejected', 'paid'] as const;
export type FnfLegacyStatus = (typeof FNF_LEGACY_STATUSES)[number];

/**
 * How a settlement made on the legacy (manual) path reads as a stage. A legacy row was created already
 * submitted — its amounts were typed in and its approval started at once — so `pending_approval` is
 * `submitted`.
 */
export function legacyStatusToStage(status: FnfLegacyStatus): FnfStage {
  switch (status) {
    case 'pending_approval':
      return 'submitted';
    case 'approved':
      return 'approved';
    case 'rejected':
      return 'rejected';
    case 'paid':
      return 'paid';
  }
}

/**
 * The legacy status written alongside every stage change, so legacy readers are never told something
 * false. The safe reading of each stage for a reader that knows only four values:
 *   - every stage BEFORE approval (`initiated`, `clearance_review`, `calculated`, `submitted`) is
 *     `pending_approval` — nothing is approved yet, and nothing may be paid;
 *   - `payment_processing` is `approved` — approved, and NOT yet paid (money may be in flight);
 *   - `cancelled` is `rejected` — it will never be paid.
 * Nothing that is not paid ever reads as `paid`, and nothing unapproved as `approved`. It round-trips:
 * `stageToLegacyStatus(legacyStatusToStage(s)) === s` for all four legacy values.
 */
export function stageToLegacyStatus(stage: FnfStage): FnfLegacyStatus {
  switch (stage) {
    case 'initiated':
    case 'clearance_review':
    case 'calculated':
    case 'submitted':
      return 'pending_approval';
    case 'approved':
    case 'payment_processing':
      return 'approved';
    case 'paid':
      return 'paid';
    case 'rejected':
    case 'cancelled':
      return 'rejected';
  }
}

/** Alias of `stageToLegacyStatus`, named for the DTO projection. Never `null`: every stage has a safe reading. */
export const legacyStatusFor = stageToLegacyStatus;

/**
 * How a settlement's figures came to be. `legacy_manual`: amounts typed into the legacy form (no
 * versions, no formula). `calculated`: produced by calculation versions on the ADR 0015 path.
 */
export const FNF_SETTLEMENT_BASES = ['legacy_manual', 'calculated'] as const;
export type FnfSettlementBasis = (typeof FNF_SETTLEMENT_BASES)[number];

// ---------------------------------------------------------------------------
// Exact decimal arithmetic (4 dp, BigInt fixed point)
// ---------------------------------------------------------------------------

const DECIMAL_SCALE = 4;
const DECIMAL_FACTOR = 10n ** BigInt(DECIMAL_SCALE);
const DECIMAL_PATTERN = /^(-)?(\d+)(?:\.(\d{1,4}))?$/;

/** A decimal string with at most 4 places as integer ten-thousandths. Throws on anything else. */
function toUnits(value: string): bigint {
  const m = DECIMAL_PATTERN.exec(value.trim());
  if (!m) throw new RangeError(`Not a decimal with at most ${DECIMAL_SCALE} places: '${value}'`);
  const [, sign, whole, frac = ''] = m;
  const units = BigInt(whole!) * DECIMAL_FACTOR + BigInt(frac.padEnd(DECIMAL_SCALE, '0'));
  return sign ? -units : units;
}

/** Canonical form: always 4 places, no `-0.0000`. */
function fromUnits(units: bigint): string {
  const negative = units < 0n;
  const abs = negative ? -units : units;
  const whole = abs / DECIMAL_FACTOR;
  const frac = (abs % DECIMAL_FACTOR).toString().padStart(DECIMAL_SCALE, '0');
  return `${negative ? '-' : ''}${whole.toString()}.${frac}`;
}

/** `a + b`, exact. Inputs have at most 4 decimal places; the result has exactly 4. */
export function addDecimal(a: string, b: string): string {
  return fromUnits(toUnits(a) + toUnits(b));
}

/** `a - b`, exact. */
export function subDecimal(a: string, b: string): string {
  return fromUnits(toUnits(a) - toUnits(b));
}

/** -1, 0 or 1. `"1.5"` and `"1.5000"` compare equal. */
export function compareDecimal(a: string, b: string): -1 | 0 | 1 {
  const x = toUnits(a);
  const y = toUnits(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

/** The sum of a list, exact; `"0.0000"` for none. */
export function sumDecimals(values: readonly string[]): string {
  return fromUnits(values.reduce((acc, v) => acc + toUnits(v), 0n));
}

/** The canonical 4-place form of a decimal string. */
export function normaliseDecimal(value: string): string {
  return fromUnits(toUnits(value));
}

// ---------------------------------------------------------------------------
// Calculation items
// ---------------------------------------------------------------------------

export const FNF_SECTIONS = [
  'earned_salary',
  'notice_adjustment',
  'leave_encashment',
  'gratuity',
  'bonus',
  'recoveries',
  'statutory_deductions',
  'tax',
  'other',
] as const;
export type FnfSection = (typeof FNF_SECTIONS)[number];

/** The SIGN of an item. Amounts are never negative: a deduction is a `deduction`, not a minus. */
export const FNF_ITEM_KINDS = ['earning', 'deduction'] as const;
export type FnfItemKind = (typeof FNF_ITEM_KINDS)[number];

/** `engine`: computed from rules and records. `manual`: entered by a person, with reason and authoriser. */
export const FNF_ITEM_ORIGINS = ['engine', 'manual'] as const;
export type FnfItemOrigin = (typeof FNF_ITEM_ORIGINS)[number];

export interface FnfFormulaInput {
  readonly name: string;
  /** The value as used, as a string (a decimal, a count of days, a date). */
  readonly value: string;
  /** Where the value came from: a compensation revision, a leave balance, a rule version, a clearance. */
  readonly source: { readonly type: string; readonly id: string | null; readonly label: string };
}

export interface FnfFormula {
  /**
   * For display, e.g. `15 × 45,000.00 × 5 ÷ 26`. GENERATED from the same `inputs` the amount was computed
   * from, so what is shown and what is paid cannot disagree.
   */
  readonly expression: string;
  readonly inputs: readonly FnfFormulaInput[];
}

export interface FnfSourceRef {
  readonly type: string;
  readonly id: string;
}

export interface FnfCalculationItem {
  readonly section: FnfSection;
  /** Stable within a version, e.g. `EARNED_BASIC`, `GRATUITY`, `RECOVERY_IT_ASSETS`. */
  readonly code: string;
  readonly label: string;
  readonly kind: FnfItemKind;
  /** Decimal STRING, never negative; `kind` decides the sign. */
  readonly amount: string;
  readonly currency: string;
  readonly origin: FnfItemOrigin;
  /** Required for a `manual` item; `null` for an engine item. */
  readonly manual: {
    readonly reason: string;
    readonly authorisedByPersonId: string;
    readonly authorisedByName: string;
  } | null;
  readonly formula: FnfFormula | null;
  readonly ruleRef: { readonly ruleCode: string; readonly ruleVersionId: string } | null;
  /**
   * The records this item SETTLES — pays out or recovers (a clearance recovery, a reimbursement, an
   * advance, a leave balance being encashed). Each may be settled once, so the duplicate guard refuses
   * a source named by two items or already settled in regular payroll. Records the amount merely READS
   * (a compensation revision, a rule version) belong in `formula.inputs[].source`, not here.
   */
  readonly sourceRefs: readonly FnfSourceRef[];
  readonly taxTreatment: {
    readonly exemptAmount: string;
    readonly taxableAmount: string;
    readonly basis: string;
  } | null;
  /** Earned salary only: the days it pays for, and the regular runs already covering part of them. */
  readonly coverage: {
    readonly fromDate: string;
    readonly toDate: string;
    readonly coveredByRunIds: readonly string[];
  } | null;
}

export interface FnfTotals {
  readonly grossEarnings: string;
  readonly totalDeductions: string;
  /** Earnings minus deductions. NEGATIVE means the employee owes the company (a recovery). */
  readonly netPayable: string;
}

/**
 * The totals of a version, exact. Throws on a negative item amount or mixed currencies — a version with
 * either is malformed, and must not be totalled into something that looks right.
 */
export function totalsFor(items: readonly FnfCalculationItem[]): FnfTotals {
  let earnings = 0n;
  let deductions = 0n;
  const currencies = new Set(items.map((i) => i.currency));
  if (currencies.size > 1) {
    throw new RangeError(
      `A settlement version has one currency, found ${[...currencies].join(', ')}`,
    );
  }
  for (const item of items) {
    const units = toUnits(item.amount);
    if (units < 0n) throw new RangeError(`Item ${item.code} has a negative amount`);
    if (item.kind === 'earning') earnings += units;
    else deductions += units;
  }
  return {
    grossEarnings: fromUnits(earnings),
    totalDeductions: fromUnits(deductions),
    netPayable: fromUnits(earnings - deductions),
  };
}

/** Per-section earnings and deductions, for the drawer's section headers. Sections with no items are omitted. */
export function sectionTotals(
  items: readonly FnfCalculationItem[],
): Partial<Record<FnfSection, { earnings: string; deductions: string }>> {
  const out: Partial<Record<FnfSection, { earnings: bigint; deductions: bigint }>> = {};
  for (const item of items) {
    const acc = (out[item.section] ??= { earnings: 0n, deductions: 0n });
    if (item.kind === 'earning') acc.earnings += toUnits(item.amount);
    else acc.deductions += toUnits(item.amount);
  }
  const result: Partial<Record<FnfSection, { earnings: string; deductions: string }>> = {};
  for (const [section, acc] of Object.entries(out) as [
    FnfSection,
    { earnings: bigint; deductions: bigint },
  ][]) {
    result[section] = { earnings: fromUnits(acc.earnings), deductions: fromUnits(acc.deductions) };
  }
  return result;
}

// ---------------------------------------------------------------------------
// Versions and their problems
// ---------------------------------------------------------------------------

export const FNF_VERSION_STATUSES = ['ready', 'incomplete'] as const;
export type FnfVersionStatus = (typeof FNF_VERSION_STATUSES)[number];

/**
 * Why a version is `incomplete`. A missing rule or record is stated, never filled with a guess: an
 * incomplete version shows what it could compute and cannot be submitted.
 */
export const FNF_PROBLEM_CODES = [
  'GRATUITY_RULE_NOT_CONFIGURED',
  'GRATUITY_SERVICE_HISTORY_INCOMPLETE',
  'LEAVE_ENCASHMENT_POLICY_MISSING',
  'WAGE_BASE_UNKNOWN',
  'COMPENSATION_MISSING',
  'NOTICE_POLICY_MISSING',
  'LAST_WORKING_DAY_MISSING',
  'STATUTORY_RULES_NOT_CONFIGURED',
  'PT_SCHEDULE_FACT_MISSING',
  'TAX_RULES_NOT_CONFIGURED',
  'DUPLICATE_COVERAGE',
  'MANUAL_ITEM_UNAUTHORISED',
  'NEGATIVE_ITEM_AMOUNT',
  'CURRENCY_MISMATCH',
] as const;
export type FnfProblemCode = (typeof FNF_PROBLEM_CODES)[number];

export interface FnfProblem {
  readonly code: FnfProblemCode;
  readonly section: FnfSection | null;
  readonly message: string;
}

export function versionStatusFor(problems: readonly FnfProblem[]): FnfVersionStatus {
  return problems.length === 0 ? 'ready' : 'incomplete';
}

/** Structural problems of a set of items: what makes a version unfit regardless of rules. */
export function itemProblems(items: readonly FnfCalculationItem[]): FnfProblem[] {
  const problems: FnfProblem[] = [];
  const currencies = new Set(items.map((i) => i.currency));
  if (currencies.size > 1) {
    problems.push({
      code: 'CURRENCY_MISMATCH',
      section: null,
      message: `Items are in more than one currency: ${[...currencies].join(', ')}`,
    });
  }
  for (const item of items) {
    if (!DECIMAL_PATTERN.test(item.amount.trim()) || toUnits(item.amount) < 0n) {
      problems.push({
        code: 'NEGATIVE_ITEM_AMOUNT',
        section: item.section,
        message: `${item.label}: an amount is never negative; the kind carries the sign`,
      });
    }
    if (
      item.origin === 'manual' &&
      (!item.manual || !item.manual.reason.trim() || !item.manual.authorisedByPersonId)
    ) {
      problems.push({
        code: 'MANUAL_ITEM_UNAUTHORISED',
        section: item.section,
        message: `${item.label}: a manual item needs a reason and the person who authorised it`,
      });
    }
  }
  return problems;
}

// ---------------------------------------------------------------------------
// Clearances
// ---------------------------------------------------------------------------

export const FNF_CLEARANCE_DEPARTMENTS = [
  'manager_handover',
  'it_assets',
  'finance',
  'facilities',
  'hr',
  'other',
] as const;
export type FnfClearanceDepartment = (typeof FNF_CLEARANCE_DEPARTMENTS)[number];

/** Who owns a clearance: the exiting employee's reporting manager, holders of a role, or one person. */
export const FNF_CLEARANCE_OWNER_RULES = ['reporting_manager', 'role', 'person'] as const;
export type FnfClearanceOwnerRule = (typeof FNF_CLEARANCE_OWNER_RULES)[number];

/**
 * What a clearance holds back.
 *   - `blocks_submission`: the settlement cannot be submitted until it is cleared (or waived by HR).
 *   - `recovery_only`: it may only add a recovery; submission waits until its amount is known, because
 *     that amount is a financial input of the version.
 *   - `informational`: recorded, never blocks.
 */
export const FNF_CLEARANCE_GATES = ['blocks_submission', 'recovery_only', 'informational'] as const;
export type FnfClearanceGate = (typeof FNF_CLEARANCE_GATES)[number];

/** `waived` is HR's (`fnf.clearance:manage`), with a reason; every other status is the owner's own. */
export const FNF_CLEARANCE_STATUSES = [
  'pending',
  'cleared',
  'cleared_with_recovery',
  'blocked',
  'waived',
] as const;
export type FnfClearanceStatus = (typeof FNF_CLEARANCE_STATUSES)[number];

export const FNF_CLEARANCE_TRANSITIONS: readonly {
  readonly from: FnfClearanceStatus;
  readonly to: FnfClearanceStatus;
  readonly by: string;
}[] = [
  { from: 'pending', to: 'cleared', by: 'owner' },
  { from: 'pending', to: 'cleared_with_recovery', by: 'owner, with the recovery amount' },
  { from: 'pending', to: 'blocked', by: 'owner, with a note' },
  { from: 'pending', to: 'waived', by: 'HR, with a reason' },
  { from: 'blocked', to: 'cleared', by: 'owner' },
  { from: 'blocked', to: 'cleared_with_recovery', by: 'owner, with the recovery amount' },
  { from: 'blocked', to: 'waived', by: 'HR, with a reason' },
];

export function isClearanceTransition(from: FnfClearanceStatus, to: FnfClearanceStatus): boolean {
  return FNF_CLEARANCE_TRANSITIONS.some((e) => e.from === from && e.to === to);
}

/** The owner has said their piece, or HR waived it. */
export function isClearanceSettled(status: FnfClearanceStatus): boolean {
  return status === 'cleared' || status === 'cleared_with_recovery' || status === 'waived';
}

export interface FnfClearanceFacts {
  readonly id: string;
  readonly department: FnfClearanceDepartment;
  readonly gate: FnfClearanceGate;
  readonly status: FnfClearanceStatus;
  /** Decimal STRING, set exactly when `status` is `cleared_with_recovery`. */
  readonly recoveryAmount: string | null;
}

export type FnfClearanceHold = 'not_cleared' | 'blocked' | 'recovery_pending';

export interface FnfClearanceVerdict {
  readonly canSubmit: boolean;
  readonly holding: readonly {
    readonly clearanceId: string;
    readonly department: FnfClearanceDepartment;
    readonly reason: FnfClearanceHold;
  }[];
  /** Recoveries the version must carry, one per `cleared_with_recovery` clearance. */
  readonly recoveries: readonly { readonly clearanceId: string; readonly amount: string }[];
}

/**
 * What the clearances hold back. `informational` never does. `blocks_submission` holds until settled.
 * `recovery_only` holds while its amount is unknown (`pending` or `blocked`).
 */
export function clearanceGateVerdict(
  clearances: readonly FnfClearanceFacts[],
): FnfClearanceVerdict {
  const holding: {
    clearanceId: string;
    department: FnfClearanceDepartment;
    reason: FnfClearanceHold;
  }[] = [];
  const recoveries: { clearanceId: string; amount: string }[] = [];
  for (const c of clearances) {
    if (c.status === 'cleared_with_recovery' && c.recoveryAmount !== null) {
      recoveries.push({ clearanceId: c.id, amount: normaliseDecimal(c.recoveryAmount) });
    }
    if (c.gate === 'informational' || isClearanceSettled(c.status)) continue;
    const reason: FnfClearanceHold =
      c.gate === 'recovery_only'
        ? 'recovery_pending'
        : c.status === 'blocked'
          ? 'blocked'
          : 'not_cleared';
    holding.push({ clearanceId: c.id, department: c.department, reason });
  }
  return { canSubmit: holding.length === 0, holding, recoveries };
}

// ---------------------------------------------------------------------------
// Submission and approval
// ---------------------------------------------------------------------------

export interface FnfVersionFacts {
  readonly id: string;
  readonly status: FnfVersionStatus;
  readonly problems: readonly FnfProblem[];
  readonly items: readonly FnfCalculationItem[];
  /** Set once a newer version exists; a superseded version is history, not a candidate. */
  readonly supersededAt: string | null;
}

export type FnfSubmitRefusal =
  | { readonly code: 'VERSION_SUPERSEDED' }
  | { readonly code: 'VERSION_INCOMPLETE'; readonly problems: readonly FnfProblem[] }
  | {
      readonly code: 'CLEARANCE_OUTSTANDING';
      readonly clearanceId: string;
      readonly department: FnfClearanceDepartment;
      readonly reason: FnfClearanceHold;
    }
  | {
      readonly code: 'CLEARANCE_RECOVERY_NOT_IN_VERSION';
      readonly clearanceId: string;
      readonly amount: string;
    };

export type FnfSubmitVerdict =
  { readonly ok: true } | { readonly ok: false; readonly refusals: readonly FnfSubmitRefusal[] };

/**
 * Whether a version may be submitted for approval. Every reason is returned, not just the first, so the
 * screen can list them. A `cleared_with_recovery` clearance must be reflected in the version as a
 * `recoveries` deduction whose `sourceRefs` name the clearance, for exactly the recorded amount — a
 * version calculated before the recovery was recorded is stale and must be recalculated.
 */
export function canSubmitVersion(
  version: FnfVersionFacts,
  clearances: readonly FnfClearanceFacts[],
): FnfSubmitVerdict {
  const refusals: FnfSubmitRefusal[] = [];
  if (version.supersededAt !== null) refusals.push({ code: 'VERSION_SUPERSEDED' });
  if (version.status !== 'ready' || version.problems.length > 0) {
    refusals.push({ code: 'VERSION_INCOMPLETE', problems: version.problems });
  }
  const verdict = clearanceGateVerdict(clearances);
  for (const h of verdict.holding) {
    refusals.push({
      code: 'CLEARANCE_OUTSTANDING',
      clearanceId: h.clearanceId,
      department: h.department,
      reason: h.reason,
    });
  }
  for (const r of verdict.recoveries) {
    const carried = version.items.filter(
      (i) =>
        i.section === 'recoveries' &&
        i.kind === 'deduction' &&
        i.sourceRefs.some((s) => s.type === 'clearance' && s.id === r.clearanceId),
    );
    const total = sumDecimals(carried.map((i) => i.amount));
    if (carried.length === 0 || compareDecimal(total, r.amount) !== 0) {
      refusals.push({
        code: 'CLEARANCE_RECOVERY_NOT_IN_VERSION',
        clearanceId: r.clearanceId,
        amount: r.amount,
      });
    }
  }
  return refusals.length === 0 ? { ok: true } : { ok: false, refusals };
}

/**
 * Whether an approval still stands: it was given, to the version that is current now, on the digest that
 * version has now. Any recalculation (a new version) or content change (a new digest) voids it.
 */
export function approvalStillValid(f: {
  readonly approvedVersionId: string | null;
  readonly currentVersionId: string | null;
  readonly approvedDigest: string | null;
  readonly currentDigest: string | null;
}): boolean {
  return (
    f.approvedVersionId !== null &&
    f.approvedDigest !== null &&
    f.approvedVersionId === f.currentVersionId &&
    f.approvedDigest === f.currentDigest
  );
}

/** The settlement stage a payment obligation's state implies, once approved; `null` for "no change". */
export function stageForObligation(state: PaymentObligationState): FnfStage | null {
  if (state === 'paid') return 'paid';
  if (state === 'batched' || state === 'in_flight' || state === 'failed')
    return 'payment_processing';
  return null;
}

// ---------------------------------------------------------------------------
// Duplicate guard
// ---------------------------------------------------------------------------

export interface RegularPayrollCoverage {
  /** Days paid by regular payroll runs (released or finalized) for this employment. */
  readonly periods: readonly {
    readonly runId: string;
    readonly fromDate: string;
    readonly toDate: string;
  }[];
  /** Source records (recoveries, reimbursements, advances) already settled through regular payroll. */
  readonly settledSources: readonly FnfSourceRef[];
}

export type FnfCoverageConflict =
  | {
      readonly kind: 'salary_days_in_payroll';
      readonly itemCode: string;
      readonly runId: string;
      readonly fromDate: string;
      readonly toDate: string;
    }
  | {
      readonly kind: 'salary_days_duplicated';
      readonly itemCodes: readonly [string, string];
      readonly fromDate: string;
      readonly toDate: string;
    }
  | { readonly kind: 'source_in_payroll'; readonly itemCode: string; readonly source: FnfSourceRef }
  | {
      readonly kind: 'source_duplicated';
      readonly itemCodes: readonly string[];
      readonly source: FnfSourceRef;
    };

function overlap(
  a: { fromDate: string; toDate: string },
  b: { fromDate: string; toDate: string },
): { fromDate: string; toDate: string } | null {
  const fromDate = a.fromDate > b.fromDate ? a.fromDate : b.fromDate;
  const toDate = a.toDate < b.toDate ? a.toDate : b.toDate;
  return fromDate <= toDate ? { fromDate, toDate } : null;
}

const sourceKey = (s: FnfSourceRef) => `${s.type}\u0000${s.id}`;

/**
 * Everything a version would pay twice: earned-salary days that a regular run already paid or that two
 * items both pay, and source records (a recovery, a reimbursement) settled in regular payroll or drawn on
 * by more than one item. Dates are inclusive `YYYY-MM-DD` strings. An empty list means no duplicate.
 */
export function overlappingCoverage(
  items: readonly FnfCalculationItem[],
  regular: RegularPayrollCoverage,
): FnfCoverageConflict[] {
  const conflicts: FnfCoverageConflict[] = [];

  const salary = items.filter((i) => i.section === 'earned_salary' && i.coverage !== null);
  for (const item of salary) {
    for (const period of regular.periods) {
      const o = overlap(item.coverage!, period);
      if (o)
        conflicts.push({
          kind: 'salary_days_in_payroll',
          itemCode: item.code,
          runId: period.runId,
          ...o,
        });
    }
  }
  for (let i = 0; i < salary.length; i++) {
    for (let j = i + 1; j < salary.length; j++) {
      const o = overlap(salary[i]!.coverage!, salary[j]!.coverage!);
      if (o) {
        conflicts.push({
          kind: 'salary_days_duplicated',
          itemCodes: [salary[i]!.code, salary[j]!.code],
          ...o,
        });
      }
    }
  }

  const settled = new Set(regular.settledSources.map(sourceKey));
  const bySource = new Map<string, { source: FnfSourceRef; codes: string[] }>();
  for (const item of items) {
    const seen = new Set<string>();
    for (const source of item.sourceRefs) {
      const key = sourceKey(source);
      if (seen.has(key)) continue;
      seen.add(key);
      if (settled.has(key))
        conflicts.push({ kind: 'source_in_payroll', itemCode: item.code, source });
      const entry = bySource.get(key) ?? { source, codes: [] };
      entry.codes.push(item.code);
      bySource.set(key, entry);
    }
  }
  for (const { source, codes } of bySource.values()) {
    if (codes.length > 1) conflicts.push({ kind: 'source_duplicated', itemCodes: codes, source });
  }
  return conflicts;
}
